<template>
  <main :aria-label="$t('users.edit_user')" class="user-edit-page">
    <CompanyBreadcrumb :aria-label="$t('users.edit_user')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="formErrors.length">
      <div v-for="(error, index) in formErrors" :key="'edit-error-' + index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: red">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>

    <div v-if="userDetail" class="card user-edit-card">
      <form action="" class="regular-form user-edit-form" enctype="multipart/form-data">
        <div class="uk-grid-collapse uk-child-width-1-2@m user-edit-form-grid" uk-grid>
          <div class="user-edit-form-col position-relative">
            <h4>
              <i class="uil-user-circle"></i>
              {{ $t("users.information") }}
            </h4>
            <div class="uk-position-relative user-edit-photo">
              <label class="col-form-label" for="user-edit-photo-input" @click.prevent="triggerPhotoInput">{{
                  $t("users.photo")
                }}</label>
              <div class="user-profile-photo m-auto rounded-circle bg-light">
                <img id="user-edit-image-holder" :src="photoPreview" alt="" class="border border-light"/>
              </div>
              <div class="uk-position-center">
                <input id="user-edit-photo-input"
                       ref="file"
                       accept="image/*"
                       class="d-none"
                       type="file"
                       @change="onChangeFile"
                       @click="onPhotoInputClick"
                />
                <button class="user-edit-photo__trigger" type="button" @click="triggerPhotoInput">
                  <span aria-hidden="true" class="icon-feather-camera icon-medium text-black"></span>
                  <span class="sr-only">{{ $t("users.photo") }}</span>
                </button>
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="name">{{
                    $t("users.firstname")
                  }}<span class="required">*</span></label>
                <input id="name" v-model="form.name" class="form-control" name="name" placeholder="" required=""
                       type="text"/>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="surname">{{
                    $t("users.lastname")
                  }}<span class="required">*</span></label>
                <input id="surname" v-model="form.surname" class="form-control" name="surname" placeholder=""
                       required="" type="text"/>
              </div>
            </div>

            <div v-if="!shouldHideEmailField" class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="mail">{{
                    $t("general.email")
                  }}<span class="required">*</span></label>
                <input id="mail" v-model="form.email" class="form-control" name="mail" placeholder="" required=""
                       type="email"/>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t("gender.gender") }}</label>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="selectedGender"
                                       :searchable="false"
                                       :show-labels="false"
                                       :values="genderOptions"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="phone">{{ $t("general.phone") }}
                  <span v-if="form.login_type_id === 2 || form.authentication_type_id === 3"
                        class="required">*</span>
                </label>
                <div class="uk-position-relative w-100">
                  <PhoneInput v-model="form.phone" :country-phone-code="form.country_phone_code" name="phone"/>
                </div>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="sicil">{{
                    $t("users.reg_no")
                  }}<span v-if="form.login_type_id === 4" class="required">*</span></label>
                <input id="sicil" v-model="form.registration_number" class="form-control" name="sicil"
                       placeholder="" type="text"/>
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="national_id">{{ $t('general.national_id') }}<span
                    v-if="form.login_type_id === 3"
                    class="required">*</span></label>
                <input id="national_id" v-model="form.national_id" class="form-control" name="national_id"
                       placeholder="" type="text"/>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="dob">{{
                    $t("users.dob")
                  }}</label>
                <input id="dob" v-model="form.dob" class="form-control" name="dob" placeholder="" required=""
                       type="date"/>
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t("profile.login_type") }}</label>
                <multiple-select-input :allow-empty="false"
                                       :model.sync="selectedLoginType"
                                       :searchable="false"
                                       :show-labels="false"
                                       :values="login_types"
                />
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t("profile.authentication_type") }}</label>
                <multiple-select-input :allow-empty="false"
                                       :model.sync="selectedAuthType"
                                       :searchable="false"
                                       :show-labels="false"
                                       :values="authentication_types"
                />
              </div>
            </div>
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t("general.notification_method") }}</label>
                <multiple-select-input :allow-empty="false"
                                       :model.sync="selectedNotification"
                                       :searchable="false"
                                       :show-labels="false"
                                       :values="notificationOptions"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t("general.language") }}</label>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="selectedLanguage"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="languageOptions"
                />
              </div>
            </div>

            <div class="form-group row mt-4">
              <div class="col-md-12">
                <h4>
                  <i class="uil-user-circle"></i>
                  {{ $t("users.user_add_to_group") }}
                </h4>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="userGroup"
                                       :multiple="true"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="formattedUserGroup"
                />
              </div>
            </div>
            <hr/>
            <div class="form-group row mt-3 input-checkbox">
              <div class="col-md-12">
                <h4>
                  <i class="uil-asterisk"></i>
                  {{ $t("users.user_password") }}
                </h4>
                <div class="user-edit-checkbox">
                  <input id="user-password" v-model="passwordStatus" checked type="checkbox"/>
                  <label for="user-password">{{
                      $t("users.user_password_auto")
                    }}</label>
                </div>
                <label class="col-form-label" for="pass">{{
                    $t("general.password")
                  }}<span class="required">*</span></label>
                <input id="pass" v-model="form.password" class="form-control" disabled name="pass" placeholder=""
                       type="password"/>
              </div>
            </div>
          </div>
          <div class="user-edit-form-col">
            <h4 v-if="$isSuperAdmin">
              <i class="uil-bag"></i>
              {{ $t('general.authorization') }}
            </h4>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="institution">
                  {{ $t('general.role_definition') }}
                </label>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="selectedRoles"
                                       :multiple="true"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="roles"
                />
              </div>
            </div>
            <div v-if="isHierarchy" class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="institution">
                  {{ $t('general.linked_manager') }}
                  <span class="required">*</span>
                </label>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="selectedUser"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="userOptions"
                />
              </div>
            </div>

            <h4 v-if="!$isSuperAdmin">
              <i class="uil-bag"></i>
              {{ $t("users.business_information") }}
            </h4>

            <div v-if="$isSuperAdmin" class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="institution">
                  {{ $t("users.institution") }}
                  <span class="required">*</span>
                </label>
                <multiple-select-input :model.sync="company"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="formattedInstitution"
                                       @select="onInstitutionSelect"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="position">{{
                    $t("users.mission")
                  }}</label>
                <multiple-select-input :model.sync="position"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :tag-placeholder="$t('general.add_tag_mission')"
                                       :taggable="true"
                                       :values="formattedPosition"
                                       @addTag="addTagToMission"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="title">{{ $t("users.title") }}</label>
                <multiple-select-input :model.sync="title"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :tag-placeholder="$t('general.add_tag_title')"
                                       :taggable="true"
                                       :values="formattedTitle"
                                       @addTag="addTagToTitles"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="region">{{ $t("users.area") }}</label>
                <multiple-select-input :model.sync="region"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :tag-placeholder="$t('general.add_tag_region')"
                                       :taggable="true"
                                       :values="formattedRegion"
                                       @addTag="addTagToRegion"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="department">{{ $t("users.department") }}</label>
                <multiple-select-input :model.sync="department"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :tag-placeholder="$t('general.add_tag_department')"
                                       :taggable="true"
                                       :values="formattedDepartment"
                                       @addTag="addTagToDepartment"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="branch">{{ $t("users.branch") }}</label>
                <multiple-select-input :model.sync="branch"
                                       :required="false"
                                       :searchable="true"
                                       :show-labels="false"
                                       :tag-placeholder="$t('general.add_tag_branch')"
                                       :taggable="true"
                                       :values="formattedBranch"
                                       @addTag="addTagToBranch"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t('general.company') }}</label>
                <CorporateSelect v-model="corporate" :company-id="company_id" :multiple="false"/>
              </div>
            </div>

            <div v-if="integrationOptions.length" class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label">{{ $t('general.integrations') || 'Entegrasyonlar' }}</label>
                <multiple-select-input :allow-empty="true"
                                       :model.sync="selectedIntegrations"
                                       :multiple="true"
                                       :searchable="true"
                                       :show-labels="false"
                                       :values="integrationOptions"
                                       label="title"
                                       track-by="type"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="uk-card-footer user-edit-form-footer">
          <button :disabled="isSubmitting"
                  class="uk-button uk-button-primary user-edit-save"
                  type="button"
                  @click="formSubmit"
          >
            <i v-if="isSubmitting" class="uil-spinner" uk-spinner="ratio: 0.8"></i>
            {{ isSubmitting ? `${$t('general.saving')}...` : $t('general.save') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import { isHierarchy } from "@/core/services/store.js";
import store from "@/core/services";

import company_module, {
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEMS as GET_INSTITUTIONS,
  ITEMS as INSTITUTIONS,
  MODULE_NAME as COMPANY_MODULE,
} from "@/core/services/store/company.module";

import user_group_module, {
  BASE_URL as USER_GROUP_BASE_URL,
  GET_ITEMS as GET_USER_GROUPS,
  ITEMS as USER_GROUPS,
  MODULE_NAME as USER_GROUP_MODULE,
} from "@/core/services/store/user_group.module";

import position_module, {
  BASE_URL as POSITION_BASE_URL,
  CREATE_ITEM as POSITION_CREATE_ITEM,
  GET_ITEMS as GET_POSITIONS,
  ITEMS as POSITIONS,
  MODULE_NAME as POSITION_MODULE,
} from "@/core/services/store/position.module";

import title_module, {
  BASE_URL as TITLE_BASE_URL,
  CREATE_ITEM as TITLE_CREATE_ITEM,
  GET_ITEMS as GET_TITLES,
  ITEMS as TITLES,
  MODULE_NAME as TITLE_MODULE,
} from "@/core/services/store/title.module";

import region_module, {
  BASE_URL as REGION_BASE_URL,
  CREATE_ITEM as REGION_CREATE_ITEM,
  GET_ITEMS as GET_REGIONS,
  ITEMS as REGIONS,
  MODULE_NAME as REGION_MODULE,
} from "@/core/services/store/region.module";

import department_module, {
  BASE_URL as DEPARTMENT_BASE_URL,
  CREATE_ITEM as DEPARTMENT_CREATE_ITEM,
  GET_ITEMS as GET_DEPARTMENTS,
  ITEMS as DEPARTMENTS,
  MODULE_NAME as DEPARTMENT_MODULE,
} from "@/core/services/store/department.module";

import branch_module, {
  BASE_URL as BRANCH_BASE_URL,
  CREATE_ITEM as BRANCH_CREATE_ITEM,
  GET_ITEMS as GET_BRANCHS,
  ITEMS as BRANCHS,
  MODULE_NAME as BRANCH_MODULE,
} from "@/core/services/store/branch.module";

import user_update_module, {
  BASE_URL as USER_UPDATE_BASE_URL,
  ERROR as USER_ERRORS,
  MODULE_NAME as USER_UPDATE_MODULE,
  SUCCESS,
  UPDATE_USER_POST,
} from "@/core/services/store/user.module";

import user_module, {
  GET_ITEM as GET_USER,
  ITEM as USER,
  MODULE_NAME as USER_MODULE,
} from "@/core/services/store/user_detail.module";

import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import PhoneInput from "@/assets/components/inputs/PhoneInput";
import CorporateSelect from "@/view/components/select/CorporateSelect.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import moment from "moment";
import axios from "axios";
import i18nService from "@/core/services/i18n.service.js";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";

const _USER_GROUP_MODULE_NAME = USER_GROUP_MODULE;
const _COMPANY_MODULE_NAME = COMPANY_MODULE;
const _POSITION_MODULE = POSITION_MODULE;
const _TITLE_MODULE = TITLE_MODULE;
const _REGION_MODULE = REGION_MODULE;
const _DEPARTMENT_MODULE = DEPARTMENT_MODULE;
const _BRANCH_MODULE = BRANCH_MODULE;
const _USER_MODULE = USER_MODULE;
const _USER_UPDATE_MODULE = USER_UPDATE_MODULE;

export default {
  name: "UserEdit",
  components: {
    MultipleSelectInput,
    PhoneInput,
    CorporateSelect,
    CompanyBreadcrumb,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_COMPANY_MODULE_NAME, company_module);
    registerStoreModule(_USER_GROUP_MODULE_NAME, user_group_module);
    registerStoreModule(_POSITION_MODULE, position_module);
    registerStoreModule(_TITLE_MODULE, title_module);
    registerStoreModule(_REGION_MODULE, region_module);
    registerStoreModule(_DEPARTMENT_MODULE, department_module);
    registerStoreModule(_BRANCH_MODULE, branch_module);
    registerStoreModule(_USER_MODULE, user_module);
    registerStoreModule(_USER_UPDATE_MODULE, user_update_module);
  },
  data() {
    return {
      users: [],
      passwordStatus: true,
      genders: [],
      languages: [],
      company: {
        id: null,
        name: null,
      },
      userGroup: [],
      position: {
        id: null,
        name: null,
      },
      title: {
        id: null,
        name: null,
      },
      region: {
        id: null,
        name: null,
      },
      department: {
        id: null,
        name: null,
      },
      branch: {
        id: null,
        name: null,
      },
      corporate: null,
      photoPreview: "",
      form: {
        name: "",
        surname: "",
        email: "",
        phone: "",
        dob: null,
        registration_number: "",
        user_group: null,
        company_id: null,
        branch_id: null,
        department_id: null,
        region_id: null,
        title_id: null,
        gender_id: null,
        position_id: null,
        corporate_id: null,
        password: "",
        login_type_id: 1,
        national_id: "",
        notification_method: "email",
        language_id: null,
        photo: null,
        country_phone_code: "",
        parent_id: "",
      },
      formErrors: [],
      company_id: null,
      isSubmitting: false,
      login_types: [],
      authentication_types: [],
      notification_methods: [
        { name: this.$t("general.notify_via_email"), value: "email" },
        { name: this.$t("general.notify_via_sms"), value: "sms" },
      ],
      roles: [],
      selectedRoles: [],
      selectedUser: null,
      selectedParentId: "",
      integrationOptions: [],
      selectedIntegrations: [],
      _waitingForFile: false,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("users.users") },
        { name: this.$t("users.edit_user") },
      ];
    },
    genderOptions() {
      return (this.genders || []).map((item) => ({
        id: item.id,
        name: this.$t("gender." + item.key),
      }));
    },
    selectedGender: {
      get() {
        return this.genderOptions.find((item) => item.id === this.form.gender_id) || null;
      },
      set(value) {
        this.form.gender_id = value ? value.id : null;
      },
    },
    selectedLoginType: {
      get() {
        return (this.login_types || []).find((item) => item.id === this.form.login_type_id) || null;
      },
      set(value) {
        if (value) {
          this.form.login_type_id = value.id;
        }
      },
    },
    selectedAuthType: {
      get() {
        return (this.authentication_types || []).find((item) => item.id === this.form.authentication_type_id) || null;
      },
      set(value) {
        if (value) {
          this.form.authentication_type_id = value.id;
        }
      },
    },
    notificationOptions() {
      return (this.notification_methods || []).map((item) => ({
        id: item.value,
        name: item.name,
      }));
    },
    selectedNotification: {
      get() {
        return this.notificationOptions.find((item) => item.id === this.form.notification_method) || null;
      },
      set(value) {
        if (value) {
          this.form.notification_method = value.id;
        }
      },
    },
    languageOptions() {
      return this.languages || [];
    },
    selectedLanguage: {
      get() {
        return this.languageOptions.find((item) => item.id === this.form.language_id) || null;
      },
      set(value) {
        this.form.language_id = value && value.id != null ? Number(value.id) : null;
      },
    },
    userOptions() {
      return (this.users || []).map((user) => ({
        ...user,
        name: `${user.name || ""} ${user.surname || ""}`.trim(),
      }));
    },
    isHierarchy() {
      return isHierarchy.value;
    },
    shouldHideEmailField() {
      const companyId = this.form?.company_id || this.company_id || this.company?.id;
      return Number(companyId) === 489;
    },
    errors() {
      return store.getters[USER_UPDATE_MODULE + "/" + USER_ERRORS];
    },
    success() {
      return store.getters[USER_UPDATE_MODULE + "/" + SUCCESS];
    },
    userDetail: {
      get() {
        return store.getters[USER_MODULE + "/" + USER];
      },
      set(value) {
      },
    },
    branchs: {
      get() {
        return store.getters[BRANCH_MODULE + "/" + BRANCHS];
      },
      set(value) {
      },
    },
    departments: {
      get() {
        return store.getters[DEPARTMENT_MODULE + "/" + DEPARTMENTS];
      },
      set(value) {
      },
    },
    regions: {
      get() {
        return store.getters[REGION_MODULE + "/" + REGIONS];
      },
      set(value) {
      },
    },
    titles: {
      get() {
        return store.getters[TITLE_MODULE + "/" + TITLES];
      },
      set(value) {
      },
    },
    positions: {
      get() {
        return store.getters[POSITION_MODULE + "/" + POSITIONS];
      },
      set(value) {
      },
    },
    user_groups: {
      get() {
        return store.getters[USER_GROUP_MODULE + "/" + USER_GROUPS];
      },
      set(value) {
      },
    },
    institutions: {
      get() {
        return store.getters[COMPANY_MODULE + "/" + INSTITUTIONS];
      },
      set(value) {
      },
    },
    formattedBranch() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.branchs === null) return [];
      this.branchs.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedDepartment() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.departments === null) return [];

      this.departments.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedRegion() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.regions === null) return [];

      this.regions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedInstitution() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.institutions === null) return [];

      this.institutions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedUserGroup() {
      let groups = [];
      if (!(this.user_groups instanceof Array)) {
        return [];
      }
      this.user_groups.forEach((item) => {
        groups.push({
          id: item.id,
          name: item.name,
        });
      });
      return groups;
    },
    formattedPosition() {
      let positions = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.positions === null) return [];

      this.positions.forEach((item) => {
        positions.push({
          id: item.id,
          name: item.name,
        });
      });
      return positions;
    },
    formattedTitle() {
      let titles = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.titles === null) return [];

      this.titles.forEach((item) => {
        titles.push({
          id: item.id,
          name: item.name,
        });
      });
      return titles;
    },
  },
  methods: {
    onInstitutionSelect(value) {
      this.form.company_id = value ? value.id : null;
      this.changeCompany();
    },
    getUsers() {
      axios.get(`${"api/user"}`, {
        headers: this.apiHeaders,
      }).then((result) => {
        this.users = result.data;
        this.selectedUser = this.userOptions.find((item) => item.id === this.selectedParentId) || null;
      });
    },
    getRoles() {
      axios
          .get(`${"api/roles"}`, {
            headers: this.apiHeaders,
          })
          .then((result) => {
            this.roles = result.data?.global;
            result.data?.company.forEach((item) => {
              this.roles.push(item);
            });
          });
    },
    addTagToMission(tag) {
      let content = this.createFromTagData(tag);

      this.$store
          .dispatch(POSITION_MODULE + "/" + POSITION_CREATE_ITEM, {
            url: POSITION_BASE_URL + "?company_id=" + this.form.company_id,
            contents: content,
          })
          .then(() => {
            this.getPositions().then((response) => {
              const addedPosition = response.find((pos) => pos.name === tag.name);

              if (addedPosition) {
                this.position = {
                  id: addedPosition.id,
                  name: addedPosition.name,
                };
                this.form.position_id = addedPosition.id;
              }
            });
          })
          .catch(() => {
            if (this.formErrors) {
              this.formErrors.push(this.$t("general.error_occurred"));
            }
          });
    },
    addTagToTitles(tag) {
      let content = this.createFromTagData(tag);

      this.$store
          .dispatch(TITLE_MODULE + "/" + TITLE_CREATE_ITEM, {
            url: TITLE_BASE_URL + "?company_id=" + this.form.company_id,
            contents: content,
          })
          .then(() => {
            this.getTitles().then((response) => {
              const addedPosition = response.find((pos) => pos.name === tag.name);

              if (addedPosition) {
                this.title = {
                  id: addedPosition.id,
                  name: addedPosition.name,
                };
                this.form.title_id = addedPosition.id;
              }
            });
          })
          .catch(() => {
            if (this.formErrors) {
              this.formErrors.push(this.$t("general.error_occurred"));
            }
          });
    },
    addTagToRegion(tag) {
      let content = this.createFromTagData(tag);

      this.$store
          .dispatch(REGION_MODULE + "/" + REGION_CREATE_ITEM, {
            url: REGION_BASE_URL + "?company_id=" + this.form.company_id,
            contents: content,
          })
          .then(() => {
            this.getRegions().then((response) => {
              const addedPosition = response.find((pos) => pos.name === tag.name);

              if (addedPosition) {
                this.region = {
                  id: addedPosition.id,
                  name: addedPosition.name,
                };
                this.form.region_id = addedPosition.id;
              }
            });
          })
          .catch(() => {
            if (this.formErrors) {
              this.formErrors.push(this.$t("general.error_occurred"));
            }
          });
    },
    addTagToDepartment(tag) {
      let content = this.createFromTagData(tag);

      this.$store
          .dispatch(DEPARTMENT_MODULE + "/" + DEPARTMENT_CREATE_ITEM, {
            url: DEPARTMENT_BASE_URL + "?company_id=" + this.form.company_id,
            contents: content,
          })
          .then(() => {
            this.getDepartments().then((response) => {
              const addedPosition = response.find((pos) => pos.name === tag.name);

              if (addedPosition) {
                this.department = {
                  id: addedPosition.id,
                  name: addedPosition.name,
                };
                this.form.department_id = addedPosition.id;
              }
            });
          })
          .catch(() => {
            if (this.formErrors) {
              this.formErrors.push(this.$t("general.error_occurred"));
            }
          });
    },
    addTagToBranch(tag) {
      let content = this.createFromTagData(tag);

      this.$store
          .dispatch(BRANCH_MODULE + "/" + BRANCH_CREATE_ITEM, {
            url: BRANCH_BASE_URL + "?company_id=" + this.form.company_id,
            contents: content,
          })
          .then(() => {
            this.getBranchs().then((response) => {
              const addedPosition = response.find((pos) => pos.name === tag.name);

              if (addedPosition) {
                this.branch = {
                  id: addedPosition.id,
                  name: addedPosition.name,
                };
                this.form.branch_id = addedPosition.id;
              }
            });
          })
          .catch(() => {
            if (this.formErrors) {
              this.formErrors.push(this.$t("general.error_occurred"));
            }
          });
    },
    createFromTagData(tag) {
      let content = new FormData();
      content.append("status", 1);
      content.append("name", tag.name);
      return content;
    },
    onPhotoInputClick(event) {
      if (!bridge.isNative) {
        return;
      }
      event.preventDefault();
      this._waitingForFile = true;
      native.pickFile("image/*");
    },
    triggerPhotoInput() {
      if (bridge.isNative) {
        this._waitingForFile = true;
        native.pickFile("image/*");
        return;
      }
      const input = this.$refs.file;
      if (!input) {
        return;
      }
      input.value = null;
      input.click();
    },
    applyPhotoFile(file) {
      if (!file) {
        return;
      }

      this.form.photo = file;
      const blob = URL.createObjectURL(file);
      this.photoPreview = blob;
      const imageHolder = $("#user-edit-image-holder");
      imageHolder.attr("src", blob);
    },
    onChangeFile() {
      this.applyPhotoFile(this.$refs.file?.files?.[0]);
    },
    getLoginTypes() {
      let types = [];
      axios
          .get(`api/user/login-types`, {
            headers: this.apiHeaders,
          })
          .then((result) => {
            result.data.forEach((data) => {
              types.push({
                id: data.id,
                name: data.name,
              });
            });
          });
      this.login_types = types;
    },
    getAuthenticationTypes() {
      let types = [];
      axios
          .get(`api/user/authentication-types`, {
            headers: this.apiHeaders,
          })
          .then((result) => {
            result.data.forEach((data) => {
              types.push({
                id: data.id,
                name: data.name,
              });
            });
          });
      this.authentication_types = types;
    },
    validateForm() {
      let self = this;
      let form = self.form;
      self.formErrors = [];

      if (form.name === null || form.name == "") {
        self.formErrors.push(self.$t("users.required.firstname"));
        self.scrollToTop();
        return false;
      }

      if (form.surname === null || form.surname == "") {
        self.formErrors.push(self.$t("users.required.lastname"));
        self.scrollToTop();
        return false;
      }

      if (!self.shouldHideEmailField && (form.email === null || form.email === "")) {
        self.formErrors.push(self.$t("users.required.email"));
        self.scrollToTop();
        return false;
      }

      let hasPhoneSelectedForAnyType =
          form.authentication_type_id === 3 || form.login_type_id === 2;

      let isPhoneRequired =
          hasPhoneSelectedForAnyType && form?.phone?.length === 0;

      if (isPhoneRequired) {
        self.formErrors.push(self.$t("users.required.phone"));
        self.scrollToTop();
        return false;
      }

      if (
          isPhoneRequired &&
          (form?.phone?.length !== 10 || Number.isInteger(form?.phone))
      ) {
        self.formErrors.push(self.$t("users.phone.invalid"));
        self.scrollToTop();
        return false;
      }

      if (
          form.login_type_id === 2 &&
          (form.phone === null || form.phone == "")
      ) {
        self.formErrors.push(self.$t("users.required.phone"));
        self.scrollToTop();
        return false;
      }

      if (
          form.login_type_id === 3 &&
          (form.national_id === null || form.national_id == "")
      ) {
        self.formErrors.push(self.$t("general.required_national_id"));
        self.scrollToTop();
        return false;
      }

      if (
          form.login_type_id === 4 &&
          (form.registration_number === null || form.registration_number == "")
      ) {
        self.formErrors.push(self.$t("users.required.reg_no"));
        self.scrollToTop();
        return false;
      }

      return true;
    },
    formSubmit() {
      let self = this;

      if (self.isSubmitting) {
        return false;
      }

      if (!self.validateForm()) {
        return false;
      }

      let form = self.form;
      form.user = form.id;

      self.isSubmitting = true;

      const parsedPhone = form.phone
          ? this.parsePhonePayload(form.phone, form.country_phone_code)
          : { phone: null, country_phone_code: form.country_phone_code || "90" };

      let form_data = new FormData();
      form_data.append("name", form.name);
      form_data.append("surname", form.surname);
      form_data.append("email", form.email);
      form_data.append("phone", parsedPhone.phone);
      form_data.append("dob", form.dob);
      form_data.append("registration_number", form.registration_number);
      form_data.append("national_id", form.national_id);
      form_data.append("country_phone_code", parsedPhone.country_phone_code);
      form_data.append("company_id", form.company_id);
      form_data.append("branch_id", form.branch_id);
      form_data.append("department_id", form.department_id);
      form_data.append("region_id", form.region_id);
      form_data.append("title_id", form.title_id);
      form_data.append("gender_id", form.gender_id);
      form_data.append("position_id", form.position_id);
      form_data.append("corporate_id", this.corporate ? this.corporate.id : null);
      form.password ? form_data.append("password", form.password) : null;
      form_data.append("login_type_id", form.login_type_id);
      form_data.append("authentication_type_id", form.authentication_type_id);
      form_data.append("notification_method", form.notification_method);
      const languageId = Number(form.language_id);
      if (Number.isInteger(languageId) && languageId > 0) {
        form_data.append("language_id", languageId);
      }
      form.photo ? form_data.append("photo", form.photo) : null;
      form_data.append("_method", "PUT");
      if (self.userGroup !== null && self.userGroup.length > 0) {
        self.userGroup.forEach((item) => {
          form_data.append("group_ids[]", item.id);
        });
      } else {
        form_data.append("group_ids[]", null);
      }
      if (self.selectedRoles && self.selectedRoles.length > 0) {
        self.selectedRoles.forEach((item) => {
          form_data.append("role_ids[]", item.id);
        });
      } else {
        form_data.append("role_ids[]", "");
      }
      form_data.append("parent_id", self.selectedUser?.id ?? null);
      if (self.selectedIntegrations && self.selectedIntegrations.length > 0) {
        self.selectedIntegrations.forEach((item) => {
          form_data.append("integration_tags[]", item.type);
        });
      } else {
        form_data.append("integration_tags", "");
      }
      store.dispatch(USER_UPDATE_MODULE + "/" + UPDATE_USER_POST, {
        url: USER_UPDATE_BASE_URL + "/update/" + this.$route.params.id,
        contents: form_data,
        returnType: "stateless",
        acceptPromise: true,
      })
          .catch((error) => {
            self.formErrors = [];

            const data = error?.response?.data || error;

            if (data?.errors) {
              Object.values(data.errors).forEach((messages) => {
                if (Array.isArray(messages)) {
                  self.formErrors.push(...messages);
                } else {
                  self.formErrors.push(messages);
                }
              });
            } else if (data?.message) {
              self.formErrors.push(data.message);
            } else if (typeof data === "string") {
              self.formErrors.push(data);
            } else {
              self.formErrors.push(self.$t("general.error_occurred"));
            }

            self.isSubmitting = false;
            self.scrollToTop();
          })
          .finally(() => {
            self.isSubmitting = false;
          });
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      });
    },
    getBranchs() {
      return this.$store.dispatch(BRANCH_MODULE + "/" + GET_BRANCHS, {
        url: BRANCH_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getDepartments() {
      return this.$store.dispatch(DEPARTMENT_MODULE + "/" + GET_DEPARTMENTS, {
        url: DEPARTMENT_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getRegions() {
      return this.$store.dispatch(REGION_MODULE + "/" + GET_REGIONS, {
        url: REGION_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getTitles() {
      return this.$store.dispatch(TITLE_MODULE + "/" + GET_TITLES, {
        url: TITLE_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getGenders() {
      axios.get(`api/genders`, { headers: this.apiHeaders })
          .then((result) => {
            this.genders = result.data.data;
          });
    },
    getSupportedLanguageCodes() {
      return (i18nService.languages || []).map((item) => String(item.lang || "").toLowerCase());
    },
    normalizeLanguageCode(item) {
      return String(item?.code || item?.language || item?.lang || "").toLowerCase();
    },
    getLanguages() {
      const supportedCodes = this.getSupportedLanguageCodes();
      axios
          .get(`api/localization`, { headers: this.apiHeaders })
          .then((result) => {
            const backendLanguages = result?.data?.languages || [];
            this.languages = backendLanguages
                .filter((item) => supportedCodes.includes(this.normalizeLanguageCode(item)))
                .map((item) => ({
                  ...item,
                  id: Number(item.id),
                }))
                .filter((item) => Number.isInteger(item.id) && item.id > 0);
          });
    },
    getPositions() {
      return this.$store.dispatch(POSITION_MODULE + "/" + GET_POSITIONS, {
        url: POSITION_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getInstitutions() {
      return this.$store.dispatch(COMPANY_MODULE + "/" + GET_INSTITUTIONS, {
        url: COMPANY_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getUserGroups() {
      this.$store.dispatch(USER_GROUP_MODULE + "/" + GET_USER_GROUPS, {
        url: USER_GROUP_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getUser() {
      this.$store.dispatch(USER_MODULE + "/" + GET_USER, {
        url: "api/user/detail" + "/" + this.$route.params.id,
      });
    },
    changeCompany() {
      if (this.userDetail.company_id) {
        this.company_id = this.userDetail.company_id;

        let temp = this.userDetail.company_id;
        this.form.company_id = null;
        this.form.company_id = temp;

        this.getPositions();
        this.getTitles();
        this.getRegions();
        this.getDepartments();
        this.getBranchs();

        this.form.position_id = "";
        this.form.title_id = "";
        this.form.region_id = "";
        this.form.department_id = "";
        this.form.branch_id = "";
      }
    },
    getIntegrationOptions() {
      axios.get("api/widgets", {
        params: { with_user_custom_list: 1 },
        headers: this.apiHeaders,
      }).then((result) => {
        const options = Array.isArray(result.data) ? [...result.data] : [];
        if (!options.some((option) => option.type === "gofluent")) {
          options.push({ type: "gofluent", title: "Go Fluent" });
        }
        this.integrationOptions = options;
        const tags = this.userDetail?.integration_tags;
        if (Array.isArray(tags)) {
          this.selectedIntegrations = options.filter((option) => tags.includes(option.type));
        }
      }).catch(() => {
        const goFluentOption = { type: "gofluent", title: "Go Fluent" };
        this.integrationOptions = [goFluentOption];
        if (this.userDetail?.integration_tags?.includes("gofluent")) {
          this.selectedIntegrations = [goFluentOption];
        }
      });
    },
    checkPhoneValidation(value) {
      const phone = value?.phone || "";
      const countryCode = String(value?.country_phone_code || "90").replace(/\D/g, "") || "90";

      if (!phone) {
        this.form.phone = "";
        this.form.country_phone_code = countryCode;
        return;
      }

      let nationalNumber = String(phone).replace(/\D/g, "");
      if (nationalNumber.startsWith(countryCode)) {
        nationalNumber = nationalNumber.slice(countryCode.length);
      }
      nationalNumber = nationalNumber.replace(/^0/, "");

      this.form.country_phone_code = countryCode;
      this.form.phone = nationalNumber
          ? `+${countryCode} ${nationalNumber}`
          : `+${countryCode} `;
    },
    parsePhonePayload(phoneValue, fallbackCountryCode = "90") {
      const raw = String(phoneValue || "").trim();
      const fallbackCode = String(fallbackCountryCode || "90").replace(/\D/g, "") || "90";

      if (!raw) {
        return { phone: null, country_phone_code: fallbackCode };
      }

      if (raw.includes(" ")) {
        const countryCode = raw.substring(0, raw.indexOf(" ")).replace(/\D/g, "") || fallbackCode;
        const nationalNumber = raw.substring(raw.indexOf(" ") + 1).replace(/\D/g, "");
        return {
          country_phone_code: countryCode,
          phone: nationalNumber.length > 5 ? nationalNumber : null,
        };
      }

      const digits = raw.replace(/\D/g, "");
      if (digits.startsWith(fallbackCode) && digits.length > fallbackCode.length) {
        return {
          country_phone_code: fallbackCode,
          phone: digits.slice(fallbackCode.length),
        };
      }

      return {
        country_phone_code: fallbackCode,
        phone: digits.length > 5 ? digits : null,
      };
    },
  },
  mounted() {
    this.getLoginTypes();
    this.getAuthenticationTypes();
    this.getInstitutions();
    this.getUserGroups();
    this.getUser();
    this.getGenders();
    this.getLanguages();
    this.getIntegrationOptions();

    if (!this.$isSuperAdmin) {
      this.form.company_id = this.userDetail.company_id;
      this.changeCompany();
    }

    if (bridge.isNative) {
      this._unsubscribePickFile = bridge.on("PICK_FILE_RESULT", (payload) => {
        if (!this._waitingForFile) return;
        this._waitingForFile = false;

        if (!payload.success || !payload.file) return;
        const { name, type, base64 } = payload.file;

        const byteString = atob(base64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        this.applyPhotoFile(new File([ab], name, { type }));
      });
    }
  },
  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },
  watch: {
    success: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.$router.push({
          name: "company.user.list",
          params: { succesMessage: this.$t("general.update_success") },
        });
      }
    },
    errors: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.formErrors.push(val);
        this.scrollToTop();
      }
    },
    passwordStatus(value) {
      if (value) {
        $("#pass").prop("disabled", true);
        this.form.password = "12345678";
      } else {
        $("#pass").prop("disabled", false);
      }
      this.form.password = "";
    },
    userDetail(value) {
      if (value && value.company) {
        this.company_id = value.company.id;
        if (this.company_id) {
          this.form = value;
          this.photoPreview = value.photo_link || "";

          this.checkPhoneValidation(value);

          if (value.dob) {
            this.form.dob = moment(this.form.dob).format("YYYY-MM-DD");
          }
          if (value.gender) {
            this.form.gender_id = value.gender.id;
          }
          if ((this.form.language_id === null || this.form.language_id === "") && value.language?.id) {
            this.form.language_id = value.language.id;
          }
          this.company = value.company;
          this.branch = value.branch;
          this.title = value.title;
          this.department = value.department;
          this.position = value.position;
          this.region = value.region;
          this.corporate = value.corporate || null;
          this.userGroup = Array.isArray(value.groups) ? value.groups : [];
          this.selectedRoles = Array.isArray(value.roles) ? [...value.roles] : [];
          this.selectedParentId = value.parent_id;
          if (value.integration_tags && Array.isArray(value.integration_tags)) {
            this.selectedIntegrations = this.integrationOptions.filter(
                (opt) => value.integration_tags.includes(opt.type)
            );
          }
          this.getBranchs();
          this.getPositions();
          this.getTitles();
          this.getRegions();
          this.getDepartments();
          this.getRoles();
          this.getUsers();
        }
      }
    },
    title(value) {
      if (value && value.id) {
        this.form.title_id = value.id;
      } else {
        this.form.title_id = null;
      }
    },
    position(value) {
      if (value && value.id) {
        this.form.position_id = value.id;
      } else {
        this.form.position_id = null;
      }
    },
    region(value) {
      if (value && value.id) {
        this.form.region_id = value.id;
      } else {
        this.form.region_id = null;
      }
    },
    department(value) {
      if (value && value.id) {
        this.form.department_id = value.id;
      } else {
        this.form.department_id = null;
      }
    },
    branch(value) {
      if (value && value.id) {
        this.form.branch_id = value.id;
      } else {
        this.form.branch_id = null;
      }
    },
    corporate(value) {
      this.form.corporate_id = value ? value.id : null;
    },
  },
};
</script>
<style scoped>
.user-edit-page {
  --user-edit-primary: var(--primaryColor, #3e416d);
  --user-edit-text: #1e293b;
  --user-edit-muted: #64748b;
  --user-edit-border: #e2e8f0;
  min-width: 0;
}

.user-edit-card {
  overflow: hidden;
  margin-top: 0;
  padding: 0;
  border: 1px solid var(--user-edit-border);
  border-radius: 14px;
  box-shadow: none;
}

.user-edit-form-col {
  padding: 32px;
}

.user-edit-form-col + .user-edit-form-col {
  border-left: 1px solid var(--user-edit-border);
}

.user-edit-form h4 {
  margin: 0 0 16px;
  color: var(--user-edit-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.user-edit-form .col-form-label {
  display: block;
  margin-bottom: 8px;
  padding: 0;
  color: var(--user-edit-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.user-edit-form .form-control {
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  color: var(--user-edit-text);
  font-size: 15px;
  line-height: 1.5;
}

.user-edit-form .form-control:focus {
  border-color: var(--user-edit-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
  outline: none;
}

.user-edit-form .form-group {
  margin-bottom: 16px;
}

.user-edit-form ::v-deep .form-group .form-group {
  margin-bottom: 0;
}

.user-edit-photo {
  margin: 16px 0 24px;
}

.user-edit-photo__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-top: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.user-edit-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.user-edit-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.user-edit-checkbox label {
  margin: 0;
  color: var(--user-edit-text);
  font-size: 14px;
  line-height: 1.4;
}

.user-edit-form-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 32px 32px;
}

.user-edit-save {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
}

.user-edit-save:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.user-edit-page .uk-alert {
  margin: 0 0 16px;
}

.user-edit-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.user-edit-page ::v-deep .multiselect,
.user-edit-page ::v-deep .custom-multiselect,
.user-edit-page ::v-deep .corporate-select .multiselect {
  min-height: 40px;
  height: auto !important;
  overflow: visible;
  box-shadow: none !important;
}

.user-edit-page ::v-deep .multiselect__tags,
.user-edit-page ::v-deep .custom-multiselect .multiselect__tags,
.user-edit-page ::v-deep .multiselect--active .multiselect__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  box-sizing: border-box;
  min-height: 38px;
  padding: 8px 40px 8px 8px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  box-shadow: none !important;
}

.user-edit-page ::v-deep .multiselect--active .multiselect__tags {
  border-color: var(--user-edit-primary);
}

.user-edit-page ::v-deep .multiselect__select {
  height: 40px;
}

.user-edit-page ::v-deep .multiselect__input,
.user-edit-page ::v-deep .multiselect__single,
.user-edit-page ::v-deep .multiselect__placeholder {
  margin-bottom: 0;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none !important;
  outline: none;
  font-size: 15px;
  line-height: 1.5;
  -webkit-appearance: none;
  appearance: none;
}

.user-edit-page ::v-deep .multiselect__input:focus,
.user-edit-page ::v-deep .multiselect__single:focus {
  background: transparent;
  border: 0;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  outline: none;
}

.user-edit-page ::v-deep .corporate-select__locked {
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  font-size: 15px;
  line-height: 1.5;
}

.user-edit-page ::v-deep .phone-input,
.user-edit-page ::v-deep input[type="tel"] {
  font-size: 15px;
}

@media (max-width: 959px) {
  .user-edit-form-col + .user-edit-form-col {
    border-top: 1px solid var(--user-edit-border);
    border-left: 0;
  }

  .user-edit-form h4 {
    font-size: 20px;
  }

  .user-edit-page .uk-alert p {
    font-size: 16px;
  }

  .user-edit-form .form-control {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .user-edit-photo__trigger {
    width: 48px;
    height: 48px;
  }

  .user-edit-checkbox {
    min-height: 48px;
  }

  .user-edit-checkbox label {
    font-size: 16px;
  }

  .user-edit-save {
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .user-edit-page ::v-deep .multiselect,
  .user-edit-page ::v-deep .custom-multiselect,
  .user-edit-page ::v-deep .corporate-select .multiselect {
    min-height: 48px;
  }

  .user-edit-page ::v-deep .multiselect__tags,
  .user-edit-page ::v-deep .custom-multiselect .multiselect__tags,
  .user-edit-page ::v-deep .multiselect--active .multiselect__tags {
    min-height: 48px;
  }

  .user-edit-page ::v-deep .multiselect__select {
    height: 48px;
  }

  .user-edit-page ::v-deep .multiselect__input,
  .user-edit-page ::v-deep .multiselect__single,
  .user-edit-page ::v-deep .multiselect__placeholder {
    font-size: 16px;
    line-height: 1.4;
  }

  .user-edit-page ::v-deep .corporate-select__locked {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .user-edit-page ::v-deep .phone-input,
  .user-edit-page ::v-deep input[type="tel"] {
    min-height: 48px;
    font-size: 16px;
  }
}

@media (max-width: 767px) {
  .user-edit-page {
    margin: 0 -8px;
    padding: 0 0 24px;
  }

  .user-edit-card {
    margin-top: 0 !important;
  }

  .user-edit-form-col {
    padding: 16px;
  }

  .user-edit-form h4 {
    font-size: 20px;
  }

  .user-edit-form-footer {
    padding: 8px 16px 16px;
  }

  .user-edit-save {
    width: 100%;
  }

  .user-edit-save:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .user-profile-photo {
    width: 120px;
    height: 120px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .user-edit-save:hover {
    opacity: 0.92;
  }

  .user-edit-photo__trigger:hover {
    background: #fff;
  }
}

@media (prefers-reduced-motion: reduce) {
  .user-edit-save:active {
    transform: none;
  }
}

input[type="file"],
input[type="file"]::-webkit-file-upload-button {
  cursor: pointer;
}
</style>

<style>
.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}
</style>
